// iter-107x: previous `eslint-disable react-hooks/immutability` directive
// removed — that rule isn't defined by eslint-plugin-react-hooks@5.2.0.
import { useState, useEffect, Fragment } from "react";
import { apiClient, useAuth } from "../App";
import { useAdminControls } from "../AdminMode";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Search, Trash2, Edit, Users, Mail, Phone, Music2, AlertCircle, Shirt, Eye, UserCheck, UserX, Info, X, Upload, Download, Send, CalendarDays } from "lucide-react";
import ImportDialog from "../components/ImportDialog";
import { PlayerPhoto } from "../components/MediaImages";
import usePullToRefresh from "../hooks/usePullToRefresh";
import useScrollRestoration from "../hooks/useScrollRestoration";
import PullToRefreshIndicator from "../components/PullToRefreshIndicator";
import SortableTableHead from "../components/SortableTableHead";
import useTableControls from "../hooks/useTableControls";

const PLAYER_TYPES = [
  { value: "member", label: "Member", description: "Full band member" },
  { value: "dep", label: "Dep", description: "Deputy/substitute player" },
  { value: "inactive", label: "Inactive", description: "Currently not playing" },
  { value: "info_only", label: "Info Only", description: "Contact info only, not playing" }
];

const PLAYER_TYPE_LABELS = {
  member: "Member",
  dep: "Dep",
  inactive: "Inactive",
  info_only: "Info Only",
};

// Iter-69: column config for sort + per-column filter dropdowns.
const PLAYERS_COLUMNS = [
  { key: "name", label: "Name", accessor: "name", sortable: true, filterable: false },
  { key: "player_type", label: "Type", accessor: "player_type", sortable: true, filterable: true, filterFormatter: (v) => PLAYER_TYPE_LABELS[v] || v || "—" },
  { key: "email", label: "Email", accessor: "email", sortable: true, filterable: false, mobileHidden: true },
  { key: "phone", label: "Phone", accessor: "phone", sortable: true, filterable: false, mobileHidden: true },
  { key: "section", label: "Section", accessor: (r) => (r.section_names && r.section_names[0]) || r.section_name || null, sortable: true, filterable: true, filterFormatter: (v) => v || "Unassigned", mobileHidden: true },
  { key: "uniform", label: "Uniform", accessor: (r) => `${r.jacket_size || ""} ${r.shirt_size || ""}`.trim() || null, sortable: false, filterable: false, mobileHidden: true },
];

const Players = () => {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter-86: preserve scroll on back-nav from a player profile / detail
  useScrollRestoration({ ready: !loading });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [viewingPlayer, setViewingPlayer] = useState(null);
  // iter-149h: dep profile — gigs attended list
  const [depGigs, setDepGigs] = useState([]);
  const [depGigsLoading, setDepGigsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  // Iter-66j: show deps toggle (default ON so admins keep current visibility)
  const [showDeps, setShowDeps] = useState(true);
  // Iter-80: bulk welcome email send
  const [pendingWelcomeCount, setPendingWelcomeCount] = useState(0);
  const [bulkWelcomeSending, setBulkWelcomeSending] = useState(false);
  // iter-107: member→dep transition state. When the edit dialog's player_type
  // changes from a non-dep value (and there's an `editingPlayer`), we fetch
  // their upcoming events so the admin can untick any they want to keep the
  // player on. Default: every event ticked (= remove from all).
  const [transitionEvents, setTransitionEvents] = useState([]); // [{id,title,start_time,...}]
  const [transitionEventsLoading, setTransitionEventsLoading] = useState(false);
  const [eventsToRemove, setEventsToRemove] = useState([]); // ids to remove from
  // iter-107: dep bulk-invite-to-events dialog state
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [bulkInviteDep, setBulkInviteDep] = useState(null);
  const [bulkInviteEvents, setBulkInviteEvents] = useState([]); // upcoming events list
  const [bulkInviteEventsLoading, setBulkInviteEventsLoading] = useState(false);
  const [bulkInviteSelected, setBulkInviteSelected] = useState([]); // event_ids
  const [bulkInviteSectionId, setBulkInviteSectionId] = useState(""); // iter-107d
  const [bulkInviteMessage, setBulkInviteMessage] = useState("");
  const [bulkInviteSubmitting, setBulkInviteSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    section_ids: [],
    parts: {},
    player_type: "member",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
    jacket_size: "",
    shirt_size: "",
    medical_info: "",
    dietary_info: "",
    home_band: "",
    known_by_player_id: "",
  });

  const isAdmin = user?.role === "admin";
  const { showAdminControls } = useAdminControls();

  useEffect(() => {
    fetchData();
  }, []);

  // Iter-80: keep the pending-welcome count fresh whenever players list changes
  useEffect(() => {
    if (isAdmin) {
      fetchPendingWelcomeCount();
    }
     
  }, [players.length, isAdmin]);

  // iter-107: when the edit dialog is open and the admin flips a *currently
  // non-dep* player's type to "dep", fetch their upcoming events so they can
  // tick which ones to unbook. Default selection is "remove from all".
  // The state is reset when the dialog closes (see Dialog onOpenChange).
  useEffect(() => {
    const wasDep = (editingPlayer?.player_type || "member") === "dep";
    const becomingDep = formData.player_type === "dep";
    if (!editingPlayer || wasDep || !becomingDep || !dialogOpen) return undefined;

    let cancelled = false;
    (async () => {
      try {
        setTransitionEventsLoading(true);
        const res = await apiClient.get(`/players/${editingPlayer.id}/upcoming-events`);
        if (cancelled) return;
        setTransitionEvents(res.data || []);
        setEventsToRemove((res.data || []).map((e) => e.id)); // default: remove from all
      } catch (err) {
        if (!cancelled) toast.error("Failed to load upcoming events");
      } finally {
        if (!cancelled) setTransitionEventsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formData.player_type, editingPlayer, dialogOpen]);

  const fetchData = async () => {
    try {
      const [playersRes, sectionsRes] = await Promise.all([
        apiClient.get("/players"),
        apiClient.get("/sections")
      ]);
      setPlayers(playersRes.data);
      setSections(sectionsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Iter-67: pull-to-refresh on mobile.
  const { pullDistance, phase, threshold } = usePullToRefresh(fetchData);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.section_ids || data.section_ids.length === 0) delete data.section_ids;
      if (!data.address) delete data.address;
      if (!data.emergency_contact_name) delete data.emergency_contact_name;
      if (!data.emergency_contact_phone) delete data.emergency_contact_phone;
      if (!data.emergency_contact_relationship) delete data.emergency_contact_relationship;
      if (!data.jacket_size) delete data.jacket_size;
      if (!data.shirt_size) delete data.shirt_size;
      if (!data.medical_info) delete data.medical_info;
      if (!data.dietary_info) delete data.dietary_info;
      // iter-149h: dep-only fields. Members shouldn't carry dep metadata,
      // so drop them from the payload when saving a non-dep player.
      if (data.player_type !== "dep") {
        delete data.home_band;
        delete data.known_by_player_id;
      } else {
        if (!data.home_band) delete data.home_band;
        if (!data.known_by_player_id) delete data.known_by_player_id;
      }

      // iter-107: member→dep transition gets its own endpoint so we can also
      // unbook the player from upcoming events in one atomic call. We only
      // take this path when:
      //   * editing an existing player (transition only makes sense on update)
      //   * AND they weren't already a dep
      //   * AND they're being flipped to "dep" in this submit
      const wasDep = (editingPlayer?.player_type || "member") === "dep";
      const becomingDep = data.player_type === "dep";
      const isTransitioning = !!editingPlayer && !wasDep && becomingDep;

      if (editingPlayer) {
        if (isTransitioning) {
          // Run the regular PUT first to persist any other field changes
          // (name/email/sections/etc), then transition. We strip
          // player_type from the PUT body to avoid two writes hitting the
          // same field.
          const { player_type: _pt, ...rest } = data;
          await apiClient.put(`/players/${editingPlayer.id}`, rest);
          const tr = await apiClient.post(
            `/players/${editingPlayer.id}/transition-to-dep`,
            { event_ids_to_remove: eventsToRemove }
          );
          const removed = tr.data?.events_removed || 0;
          toast.success(
            removed > 0
              ? `Player moved to dep. Removed from ${removed} upcoming event${removed === 1 ? "" : "s"}.`
              : "Player moved to dep."
          );
        } else {
          await apiClient.put(`/players/${editingPlayer.id}`, data);
          toast.success("Player updated successfully");
        }
      } else {
        await apiClient.post("/players", data);
        toast.success("Player added successfully");
      }
      setDialogOpen(false);
      setEditingPlayer(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save player");
    }
  };

  const handleDelete = async (playerId) => {
    if (!confirm("Are you sure you want to remove this player?")) return;
    try {
      await apiClient.delete(`/players/${playerId}`);
      toast.success("Player removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove player");
    }
  };

  const handleResendWelcome = async (player) => {
    if (!player.email) {
      toast.error("This player has no email address on file");
      return;
    }
    if (!confirm(`Send the welcome email to ${player.email}? A new temporary password will be generated.`)) return;
    try {
      await apiClient.post(`/players/${player.id}/resend-welcome`);
      toast.success(`Welcome email sent to ${player.email}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send welcome email");
    }
  };

  // iter-107: admin opens the bulk "invite this dep to multiple events" flow.
  const openBulkInvite = async (depPlayer) => {
    setBulkInviteDep(depPlayer);
    setBulkInviteSelected([]);
    setBulkInviteMessage("");
    setBulkInviteEvents([]);
    // iter-107d: default the section to the dep's primary IF they have
    // exactly one section (so admin doesn't have to click). With multiple
    // sections we leave it blank — admin must explicitly choose.
    const sids = depPlayer.section_ids || [];
    setBulkInviteSectionId(sids.length === 1 ? sids[0] : "");
    setBulkInviteOpen(true);
    try {
      setBulkInviteEventsLoading(true);
      // Re-use the same upcoming-events endpoint — it returns the dep's
      // current attendance status per event, useful UX hint for the admin.
      const res = await apiClient.get(`/players/${depPlayer.id}/upcoming-events`);
      setBulkInviteEvents(res.data || []);
    } catch {
      toast.error("Failed to load upcoming events");
    } finally {
      setBulkInviteEventsLoading(false);
    }
  };

  const submitBulkInvite = async () => {
    if (!bulkInviteDep || bulkInviteSelected.length === 0) {
      toast.error("Select at least one event");
      return;
    }
    if (!bulkInviteSectionId) {
      toast.error("Please select the section this dep will play in");
      return;
    }
    try {
      setBulkInviteSubmitting(true);
      const res = await apiClient.post(
        `/players/${bulkInviteDep.id}/dep-invite-events`,
        {
          event_ids: bulkInviteSelected,
          section_id: bulkInviteSectionId,
          message: bulkInviteMessage || undefined,
        }
      );
      const created = res.data?.created_count || 0;
      const skipped = res.data?.skipped_count || 0;
      if (created > 0) {
        toast.success(
          `Sent ${created} invitation${created === 1 ? "" : "s"} to ${bulkInviteDep.name}` +
          (skipped > 0 ? ` (${skipped} skipped — already invited)` : "")
        );
      } else if (skipped > 0) {
        toast.info(`No new invitations sent — ${bulkInviteDep.name} was already invited to all selected events.`);
      }
      setBulkInviteOpen(false);
      setBulkInviteDep(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitations");
    } finally {
      setBulkInviteSubmitting(false);
    }
  };

  const fetchPendingWelcomeCount = async () => {
    try {
      const res = await apiClient.get("/players/welcome-emails/pending");
      setPendingWelcomeCount(res.data.count || 0);
    } catch {
      setPendingWelcomeCount(0);
    }
  };

  const handleBulkSendWelcomes = async (count) => {
    if (count <= 0) {
      toast.info("No players need a welcome email — everyone's already received one.");
      return;
    }
    if (!confirm(`Send welcome emails to ${count} player${count === 1 ? "" : "s"}? Each will receive a fresh temporary password.`)) return;
    try {
      setBulkWelcomeSending(true);
      const res = await apiClient.post("/players/welcome-emails/bulk-send");
      const queued = res.data?.queued || 0;
      const errors = res.data?.errors || [];
      if (queued > 0) {
        toast.success(`Welcome emails sent to ${queued} player${queued === 1 ? "" : "s"}`);
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} failed — check the player list and retry individually.`);
      }
      fetchData();
      fetchPendingWelcomeCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk send failed");
    } finally {
      setBulkWelcomeSending(false);
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setFormData({
      name: player.name,
      email: player.email,
      phone: player.phone || "",
      address: player.address || "",
      section_ids: player.section_ids || (player.section_id ? [player.section_id] : []),
      parts: player.parts || {},
      player_type: player.player_type || "member",
      emergency_contact_name: player.emergency_contact_name || "",
      emergency_contact_phone: player.emergency_contact_phone || "",
      emergency_contact_relationship: player.emergency_contact_relationship || "",
      jacket_size: player.jacket_size || "",
      shirt_size: player.shirt_size || "",
      medical_info: player.medical_info || "",
      dietary_info: player.dietary_info || "",
      home_band: player.home_band || "",
      known_by_player_id: player.known_by_player_id || "",
    });
    setDialogOpen(true);
  };

  const handleView = (player) => {
    setViewingPlayer(player);
    setViewDialogOpen(true);
    // iter-149h: preload the dep's gig history when opening the profile.
    if ((player?.player_type || "member") === "dep") {
      setDepGigs([]);
      setDepGigsLoading(true);
      apiClient
        .get(`/players/${player.id}/gigs`)
        .then((res) => setDepGigs(res.data?.gigs || []))
        .catch(() => setDepGigs([]))
        .finally(() => setDepGigsLoading(false));
    } else {
      setDepGigs([]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      section_ids: [],
      parts: {},
      player_type: "member",
      emergency_contact_name: "",
      emergency_contact_phone: "",
      emergency_contact_relationship: "",
      jacket_size: "",
      shirt_size: "",
      medical_info: "",
      dietary_info: "",
      home_band: "",
      known_by_player_id: "",
    });
  };

  const toggleSection = (sectionId) => {
    setFormData(prev => {
      const isRemoving = prev.section_ids.includes(sectionId);
      const nextIds = isRemoving
        ? prev.section_ids.filter(id => id !== sectionId)
        : [...prev.section_ids, sectionId];
      const nextParts = { ...(prev.parts || {}) };
      if (isRemoving) delete nextParts[sectionId];
      return { ...prev, section_ids: nextIds, parts: nextParts };
    });
  };

  const setPartFor = (sectionId, partName) => {
    setFormData(prev => {
      const nextParts = { ...(prev.parts || {}) };
      if (!partName) delete nextParts[sectionId];
      else nextParts[sectionId] = partName;
      return { ...prev, parts: nextParts };
    });
  };

  const getPlayerSections = (player) => {
    // Support both new (section_names array) and old (section_name string) format
    if (player.section_names && player.section_names.length > 0) {
      return player.section_names;
    }
    if (player.section_name) {
      return [player.section_name];
    }
    return [];
  };

  const getPlayerTypeBadge = (type) => {
    switch (type) {
      case "member":
        return <Badge className="bg-green-500/20 text-green-400"><UserCheck className="w-3 h-3 mr-1" />Member</Badge>;
      case "dep":
        return <Badge className="bg-blue-500/20 text-blue-400"><Users className="w-3 h-3 mr-1" />Dep</Badge>;
      case "inactive":
        return <Badge className="bg-gray-500/20 text-gray-400"><UserX className="w-3 h-3 mr-1" />Inactive</Badge>;
      case "info_only":
        return <Badge className="bg-purple-500/20 text-purple-400"><Info className="w-3 h-3 mr-1" />Info Only</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  // iter-57: download band roster as a CSV matching the import template.
  const handleExportPlayers = async () => {
    try {
      const res = await apiClient.get("/players.csv", { responseType: "blob" });
      // Try to read the filename out of Content-Disposition; fall back to a sensible default.
      const cd = res.headers?.["content-disposition"] || res.headers?.get?.("content-disposition") || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match ? match[1] : `players_${new Date().toISOString().slice(0, 10)}.csv`;
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Players exported");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export players");
    }
  };

  // Iter-78: replaced the "All / type-filter / Show deps toggle" UX with 4
  // explicit tabs: Members | Deps | Info Only | Inactive. Inactive +
  // Info Only tabs are admin-only. The legacy filterType / showDeps state
  // is still wired so the per-column filter still works, but the tab
  // drives the primary filter now.
  const [activeTab, setActiveTab] = useState("member");
  // Filter players
  const filteredPlayers = players.filter(player => {
    const playerSections = getPlayerSections(player);
    const matchesSearch = player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      playerSections.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));

    // Primary filter: tab
    const playerType = player.player_type || "member";
    if (playerType !== activeTab) return false;

    // Admins-only tabs: defensive guard in case state somehow lands on an
    // admin tab for a non-admin (e.g. if role was demoted mid-session).
    // iter-81: gate on isAdmin (role) not showAdminControls — admins
    // should see content of all 4 tabs even in Member View.
    if ((activeTab === "inactive" || activeTab === "info_only") && !isAdmin) {
      return false;
    }

    // Legacy secondary filter (per-column filterType) — when set to "all"
    // (default) it's a no-op; otherwise still allows narrowing inside a tab.
    const matchesType = filterType === "all" || player.player_type === filterType;

    return matchesSearch && matchesType;
  });

  // Iter-69→70: sort + per-column filter state. Filters DO NOT switch the
  // view (so section grouping stays — iter-70 user request). Only an
  // active SORT flattens the table.
  const playersCtl = useTableControls(filteredPlayers, PLAYERS_COLUMNS);
  const sortActive = playersCtl.sortKey && playersCtl.sortDir !== "none";
  const useFlatView = sortActive;

  // Iter-69: render a single player row. Used by both the grouped view
  // and the flat (sorted/filtered) view to keep the cell markup in one place.
  const renderPlayerRow = (player) => (
    <TableRow key={player.id} className="border-white/5">
      <TableCell className="font-medium">
        <div className="flex items-start gap-2 md:gap-3">
          <div className="shrink-0">
            <PlayerPhoto
              playerId={player.id}
              className="w-7 h-7 md:w-9 md:h-9 rounded-full"
              fallback={
                <div className="w-7 h-7 md:w-9 md:h-9 rounded-full bg-secondary border border-white/5 flex items-center justify-center text-xs font-semibold text-muted-foreground">
                  {(player.name || "?").trim().slice(0, 1).toUpperCase()}
                </div>
              }
            />
          </div>
          <div className="flex items-center gap-2 min-w-0 pt-1.5">
            <span className="break-words">{player.name}</span>
            {showAdminControls && player.emergency_contact_name && (
              <AlertCircle className="w-3 h-3 text-green-400 shrink-0" title="Emergency contact on file" />
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>{getPlayerTypeBadge(player.player_type)}</TableCell>
      <TableCell className="hidden md:table-cell">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Mail className="w-3 h-3" />
          {player.email}
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {player.phone ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="w-3 h-3" />
            {player.phone}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {getPlayerSections(player).length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {(player.section_ids && player.section_ids.length ? player.section_ids : []).map((sid, idx) => {
              const section = sections.find((s) => s.id === sid);
              const name = section?.name || (player.section_names || [])[idx] || "Unknown";
              const part = (player.parts || {})[sid];
              return (
                <Badge
                  key={sid}
                  variant="outline"
                  className="border-primary/30 text-primary text-xs"
                  data-testid={`player-section-${player.id}-${sid}`}
                >
                  {name}
                  {part && <span className="ml-1 text-amber-400 font-normal">· {part}</span>}
                </Badge>
              );
            })}
            {(!player.section_ids || player.section_ids.length === 0) &&
              getPlayerSections(player).map((section, idx) => (
                <Badge key={idx} variant="outline" className="border-primary/30 text-primary text-xs">
                  {section}
                </Badge>
              ))}
          </div>
        ) : (
          <Badge variant="outline" className="border-muted-foreground/30">Unassigned</Badge>
        )}
      </TableCell>
      {showAdminControls && (
        <TableCell className="hidden md:table-cell">
          <div className="flex gap-1">
            {player.jacket_size && <Badge variant="outline" className="text-xs">J: {player.jacket_size}</Badge>}
            {player.shirt_size && <Badge variant="outline" className="text-xs">S: {player.shirt_size}</Badge>}
            {!player.jacket_size && !player.shirt_size && <span className="text-muted-foreground">—</span>}
          </div>
        </TableCell>
      )}
      <TableCell className="text-right">
        <div className="flex justify-end gap-0 md:gap-2 [&>button]:h-6 [&>button]:w-6 [&_svg]:w-3.5 [&_svg]:h-3.5 md:[&>button]:h-9 md:[&>button]:w-9 md:[&_svg]:w-4 md:[&_svg]:h-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleView(player)}
            data-testid={`view-player-${player.id}`}
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </Button>
          {showAdminControls && player.player_type === "dep" && (
            <Button
              variant="ghost"
              size="icon"
              className="text-blue-400 hover:text-blue-300"
              onClick={() => openBulkInvite(player)}
              data-testid={`invite-dep-events-${player.id}`}
              title="Invite this dep to one or more upcoming events"
            >
              <Mail className="w-4 h-4" />
            </Button>
          )}
          {showAdminControls && (
            <>
              <Button variant="ghost" size="icon" onClick={() => handleEdit(player)} data-testid={`edit-player-${player.id}`}>
                <Edit className="w-4 h-4" />
              </Button>
              {player.email && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-blue-400 hover:text-blue-300"
                  onClick={() => handleResendWelcome(player)}
                  data-testid={`resend-welcome-${player.id}`}
                  title="Send welcome email"
                >
                  <Send className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={() => handleDelete(player.id)}
                data-testid={`delete-player-${player.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  // Iter-66j: group filtered players by section for the table render.
  // - Primary section = first id in section_ids if available, otherwise the
  //   first name in section_names.
  // - Sections are ordered by the band's section sort order (sections array).
  // - Players with no section land in a final "Unassigned" group.
  const playersBySection = (() => {
    const sectionOrder = sections.map((s) => s.id);
    const groupMap = new Map(); // section_id_or_key -> { id, name, rows[] }

    const ensureGroup = (key, name, id = null) => {
      if (!groupMap.has(key)) groupMap.set(key, { key, id, name, rows: [] });
      return groupMap.get(key);
    };

    // Iter-70: when a column filter is active, group the filtered (processed)
    // rows so the section grouping reflects what's actually visible.
    const sourceRows = playersCtl.activeFilterCount > 0 ? playersCtl.processedRows : filteredPlayers;
    for (const p of sourceRows) {
      const primaryId = (p.section_ids && p.section_ids[0]) || null;
      const primaryName = primaryId
        ? (sections.find((s) => s.id === primaryId)?.name || (p.section_names || [])[0])
        : (p.section_names || [])[0];

      if (primaryId) {
        ensureGroup(primaryId, primaryName || "Unknown", primaryId).rows.push(p);
      } else if (primaryName) {
        ensureGroup(`__name:${primaryName}`, primaryName).rows.push(p);
      } else {
        ensureGroup("__unassigned", "Unassigned").rows.push(p);
      }
    }

    // Sort groups: known sections by band order, then alpha-named groups,
    // then Unassigned at the very end.
    const groups = Array.from(groupMap.values());
    groups.sort((a, b) => {
      if (a.key === "__unassigned") return 1;
      if (b.key === "__unassigned") return -1;
      const aIdx = a.id ? sectionOrder.indexOf(a.id) : -1;
      const bIdx = b.id ? sectionOrder.indexOf(b.id) : -1;
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
    // Sort rows alphabetically within each group
    groups.forEach((g) => g.rows.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    return groups;
  })();

  // Stats
  const memberCount = players.filter(p => p.player_type === "member").length;
  const depCount = players.filter(p => p.player_type === "dep").length;
  const inactiveCount = players.filter(p => p.player_type === "inactive").length;
  const infoOnlyCount = players.filter(p => p.player_type === "info_only").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading players...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PullToRefreshIndicator pullDistance={pullDistance} phase={phase} threshold={threshold} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="overline mb-1">Roster</p>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Players</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your band members and deps</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial min-w-[160px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-full sm:w-48 bg-secondary/50 border-white/[0.06] h-10"
              data-testid="search-players-input"
            />
          </div>

          {/* Iter-78: filterType dropdown + Show-deps switch replaced by the
              tabs below. Kept the dropdown internally only for the existing
              per-column filter wiring (defaults to "all" and stays hidden). */}

          {showAdminControls && (
            <>
              <Button 
                variant="outline" 
                className="border-white/[0.06] h-10"
                onClick={() => setImportDialogOpen(true)}
                data-testid="import-players-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Button
                variant="outline"
                className="border-white/[0.06] h-10"
                onClick={handleExportPlayers}
                data-testid="export-players-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              {pendingWelcomeCount > 0 && (
                <Button
                  variant="outline"
                  className="border-blue-400/40 text-blue-300 hover:bg-blue-500/10 h-10"
                  onClick={() => handleBulkSendWelcomes(pendingWelcomeCount)}
                  disabled={bulkWelcomeSending}
                  data-testid="bulk-send-welcomes-btn"
                  title="Send the welcome email to every player who hasn't received one yet"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {bulkWelcomeSending ? "Sending…" : `Send Welcomes (${pendingWelcomeCount})`}
                </Button>
              )}
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) {
                  setEditingPlayer(null);
                  resetForm();
                  // iter-107: clear transition-to-dep state when dialog closes
                  setTransitionEvents([]);
                  setEventsToRemove([]);
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground btn-glow h-10" data-testid="add-player-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Player
                  </Button>
                </DialogTrigger>
              <DialogContent className="glass border-white/10 !w-[calc(100vw-1rem)] !max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {editingPlayer ? "Edit Player" : "Add New Player"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                  <Tabs defaultValue="basic" className="w-full">
                    <TabsList className="bg-secondary/50 mb-4 flex flex-wrap gap-1 h-auto w-full sm:w-fit">
                      <TabsTrigger value="basic" className="flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                        Basic Info
                      </TabsTrigger>
                      {formData.player_type === "dep" && (
                        <TabsTrigger value="depinfo" className="flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-dep-info">
                          Dep Info
                        </TabsTrigger>
                      )}
                      <TabsTrigger value="emergency" className="flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Emergency
                      </TabsTrigger>
                      <TabsTrigger value="uniform" className="flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                        <Shirt className="w-4 h-4 mr-2" />
                        Uniform
                      </TabsTrigger>
                    </TabsList>

                    {/* Basic Info Tab */}
                    <TabsContent value="basic" className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Full Name *</Label>
                          <Input
                            data-testid="player-name-input"
                            placeholder="John Smith"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="bg-secondary/50"
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Player Type *</Label>
                          <Select 
                            value={formData.player_type} 
                            onValueChange={(value) => setFormData({ ...formData, player_type: value })}
                          >
                            <SelectTrigger className="bg-secondary/50" data-testid="player-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PLAYER_TYPES.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                  <div className="flex flex-col">
                                    <span>{type.label}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* iter-107: member→dep transition — pick events to
                          remove the player from. Only renders when the edit
                          dialog is open, the player wasn't already a dep,
                          and the admin has just flipped them to "dep". */}
                      {!!editingPlayer
                        && (editingPlayer.player_type || "member") !== "dep"
                        && formData.player_type === "dep" && (
                        <div
                          className="rounded-lg border border-blue-400/30 bg-blue-500/5 p-4 space-y-3"
                          data-testid="transition-to-dep-panel"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-blue-300">
                                Remove from upcoming events
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Untick any event the band has agreed they should still play.
                                Default: remove from all.
                              </p>
                            </div>
                            <div className="flex gap-2 text-xs">
                              <button
                                type="button"
                                className="text-blue-300 hover:text-blue-200 underline"
                                onClick={() => setEventsToRemove(transitionEvents.map((e) => e.id))}
                                data-testid="transition-select-all"
                              >
                                Select all
                              </button>
                              <span className="text-muted-foreground">|</span>
                              <button
                                type="button"
                                className="text-blue-300 hover:text-blue-200 underline"
                                onClick={() => setEventsToRemove([])}
                                data-testid="transition-clear-all"
                              >
                                Clear
                              </button>
                            </div>
                          </div>

                          {transitionEventsLoading ? (
                            <p className="text-xs text-muted-foreground">Loading upcoming events…</p>
                          ) : transitionEvents.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              No upcoming events — they&apos;ll just be flipped to dep.
                            </p>
                          ) : (
                            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                              {transitionEvents.map((ev) => {
                                const checked = eventsToRemove.includes(ev.id);
                                const when = ev.start_time
                                  ? new Date(ev.start_time).toLocaleString("en-GB", {
                                      weekday: "short", day: "numeric", month: "short",
                                      hour: "2-digit", minute: "2-digit",
                                    })
                                  : "";
                                return (
                                  <label
                                    key={ev.id}
                                    className="flex items-start gap-3 cursor-pointer rounded p-2 hover:bg-white/5"
                                    data-testid={`transition-event-${ev.id}`}
                                  >
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={(v) => {
                                        setEventsToRemove((prev) => (
                                          v
                                            ? [...new Set([...prev, ev.id])]
                                            : prev.filter((id) => id !== ev.id)
                                        ));
                                      }}
                                      data-testid={`transition-event-cb-${ev.id}`}
                                    />
                                    <div className="flex-1 text-sm">
                                      <p className="font-medium">
                                        {ev.title}
                                        <Badge variant="outline" className="ml-2 text-[10px]">
                                          {ev.event_type}
                                        </Badge>
                                        {ev.attendance_status && (
                                          <Badge variant="outline" className="ml-1 text-[10px] capitalize">
                                            {ev.attendance_status.replace(/_/g, " ")}
                                          </Badge>
                                        )}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {when}{ev.location ? ` · ${ev.location}` : ""}
                                      </p>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {eventsToRemove.length} of {transitionEvents.length} selected for removal.
                          </p>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <Label>Email Address *</Label>
                        <Input
                          data-testid="player-email-input"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="bg-secondary/50"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          data-testid="player-phone-input"
                          type="tel"
                          placeholder="+44 7123 456789"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="bg-secondary/50"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Address</Label>
                        <Input
                          data-testid="player-address-input"
                          placeholder="12 Music Lane, Bristol BS1 2AB"
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          className="bg-secondary/50"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Primary Section</Label>
                        <Select
                          value={formData.section_ids[0] || "__none__"}
                          onValueChange={(value) => {
                            setFormData((prev) => {
                              const next = [...prev.section_ids];
                              const newPrimary = value === "__none__" ? null : value;
                              const secondary = next[1] || null;
                              const result = [];
                              if (newPrimary) result.push(newPrimary);
                              if (secondary && secondary !== newPrimary) result.push(secondary);
                              // Clean orphaned parts entries
                              const nextParts = { ...(prev.parts || {}) };
                              Object.keys(nextParts).forEach((sid) => {
                                if (!result.includes(sid)) delete nextParts[sid];
                              });
                              return { ...prev, section_ids: result, parts: nextParts };
                            });
                          }}
                        >
                          <SelectTrigger className="bg-secondary/50" data-testid="primary-section-select">
                            <SelectValue placeholder="— No primary section —" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— No primary section —</SelectItem>
                            {sections.map((section) => (
                              <SelectItem key={section.id} value={section.id}>
                                {section.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Secondary Section <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                        <Select
                          value={formData.section_ids[1] || "__none__"}
                          onValueChange={(value) => {
                            setFormData((prev) => {
                              const primary = prev.section_ids[0] || null;
                              const newSecondary = value === "__none__" ? null : value;
                              const result = [];
                              if (primary) result.push(primary);
                              if (newSecondary && newSecondary !== primary) result.push(newSecondary);
                              const nextParts = { ...(prev.parts || {}) };
                              Object.keys(nextParts).forEach((sid) => {
                                if (!result.includes(sid)) delete nextParts[sid];
                              });
                              return { ...prev, section_ids: result, parts: nextParts };
                            });
                          }}
                          disabled={!formData.section_ids[0]}
                        >
                          <SelectTrigger className="bg-secondary/50" data-testid="secondary-section-select">
                            <SelectValue placeholder="— No secondary section —" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— No secondary section —</SelectItem>
                            {sections
                              .filter((s) => s.id !== formData.section_ids[0])
                              .map((section) => (
                                <SelectItem key={section.id} value={section.id}>
                                  {section.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {!formData.section_ids[0] && (
                          <p className="text-xs text-muted-foreground">Pick a primary section first.</p>
                        )}
                      </div>

                      {/* Parts assignment for selected sections */}
                      {formData.section_ids.length > 0 && (
                        <div className="space-y-2 p-3 rounded-lg bg-secondary/30 border border-white/10">
                          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Parts (optional)</Label>
                          {formData.section_ids.map((sectionId) => {
                            const section = sections.find((s) => s.id === sectionId);
                            if (!section) return null;
                            const sectionParts = (section.parts || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                            if (sectionParts.length === 0) return null;
                            return (
                              <div key={sectionId} className="flex items-center gap-3">
                                <span className="text-sm flex-1">{section.name}</span>
                                <Select
                                  value={formData.parts?.[sectionId] || "__none__"}
                                  onValueChange={(v) => setPartFor(sectionId, v === "__none__" ? "" : v)}
                                >
                                  <SelectTrigger
                                    className="h-8 text-xs w-40 bg-secondary/50"
                                    data-testid={`part-select-${sectionId}`}
                                  >
                                    <SelectValue placeholder="— Part —" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">— Not set —</SelectItem>
                                    {sectionParts.map((p) => (
                                      <SelectItem key={p.id || p.name} value={p.name}>
                                        {p.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </TabsContent>

                    {/* Dep Info Tab (dep only, iter-149h) */}
                    {formData.player_type === "dep" && (
                      <TabsContent value="depinfo" className="space-y-4">
                        <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 mb-4">
                          <p className="text-sm text-muted-foreground">
                            Optional admin-only notes to help track this dep — the band they normally play with, and which of your members knows them best (their referrer).
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="home_band">Home band</Label>
                          <Input
                            id="home_band"
                            placeholder="e.g. Yorkshire Building Society Band"
                            value={formData.home_band}
                            onChange={(e) => setFormData({ ...formData, home_band: e.target.value })}
                            data-testid="dep-home-band"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="known_by">Known by</Label>
                          <Select
                            value={formData.known_by_player_id || "__none"}
                            onValueChange={(v) => setFormData({ ...formData, known_by_player_id: v === "__none" ? "" : v })}
                          >
                            <SelectTrigger id="known_by" data-testid="dep-known-by">
                              <SelectValue placeholder="Pick a member…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">— None —</SelectItem>
                              {players
                                .filter((p) => (p.player_type || "member") === "member" && p.is_active !== false)
                                .filter((p) => p.id !== editingPlayer?.id)
                                .map((p) => (
                                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </TabsContent>
                    )}

                    {/* Emergency Contact Tab */}
                    <TabsContent value="emergency" className="space-y-4">
                      <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
                        <div className="flex items-center gap-2 text-red-400 mb-2">
                          <AlertCircle className="w-4 h-4" />
                          <span className="font-medium">Emergency Contact Details</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          This information is only visible to band administrators and will be used in case of emergency.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Contact Name</Label>
                        <Input
                          data-testid="emergency-name-input"
                          placeholder="Jane Smith"
                          value={formData.emergency_contact_name}
                          onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                          className="bg-secondary/50"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Contact Phone</Label>
                        <Input
                          data-testid="emergency-phone-input"
                          type="tel"
                          placeholder="+44 7123 456789"
                          value={formData.emergency_contact_phone}
                          onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                          className="bg-secondary/50"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Relationship</Label>
                        <Input
                          data-testid="emergency-relationship-input"
                          placeholder="e.g., Spouse, Parent, Partner"
                          value={formData.emergency_contact_relationship}
                          onChange={(e) => setFormData({ ...formData, emergency_contact_relationship: e.target.value })}
                          className="bg-secondary/50"
                        />
                      </div>
                    </TabsContent>

                    {/* Uniform Tab */}
                    <TabsContent value="uniform" className="space-y-4">
                      <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 mb-4">
                        <div className="flex items-center gap-2 text-primary mb-2">
                          <Shirt className="w-4 h-4" />
                          <span className="font-medium">Uniform Sizes</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Record player uniform sizes for ordering and inventory management.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Jacket Size</Label>
                          <Input
                            data-testid="jacket-size-input"
                            placeholder="e.g., M, L, 42R, etc."
                            value={formData.jacket_size}
                            onChange={(e) => setFormData({ ...formData, jacket_size: e.target.value })}
                            className="bg-secondary/50"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Shirt Size</Label>
                          <Input
                            data-testid="shirt-size-input"
                            placeholder="e.g., S, M, 15.5, etc."
                            value={formData.shirt_size}
                            onChange={(e) => setFormData({ ...formData, shirt_size: e.target.value })}
                            className="bg-secondary/50"
                          />
                        </div>
                      </div>

                      <Separator className="bg-white/10 my-2" />
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Medical / dietary info is <strong>admin-only</strong> and used for catered events &amp; trips. Only band admins can see this.
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="medical_info">Medical / accessibility</Label>
                        <Input
                          id="medical_info"
                          data-testid="medical-info-input"
                          placeholder="e.g. nut allergy, EpiPen in case; step-free access needed"
                          value={formData.medical_info}
                          onChange={(e) => setFormData({ ...formData, medical_info: e.target.value })}
                          className="bg-secondary/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dietary_info">Dietary requirements</Label>
                        <Input
                          id="dietary_info"
                          data-testid="dietary-info-input"
                          placeholder="e.g. vegetarian, gluten-free, no shellfish"
                          value={formData.dietary_info}
                          onChange={(e) => setFormData({ ...formData, dietary_info: e.target.value })}
                          className="bg-secondary/50"
                        />
                      </div>
                    </TabsContent>
                  </Tabs>
                  
                  <Separator className="bg-white/10" />
                  
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-primary text-primary-foreground" data-testid="save-player-btn">
                      {editingPlayer ? "Update Player" : "Add Player"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            
            <ImportDialog
              open={importDialogOpen}
              onOpenChange={setImportDialogOpen}
              importType="players"
              onSuccess={fetchData}
            />
            </>
          )}
        </div>
      </div>

      {/* Stats — compact on mobile, full size on md+ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card className="glass-card">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] sm:text-sm text-muted-foreground">Members</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-0.5 sm:mt-1">{memberCount}</p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-4 h-4 sm:w-6 sm:h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-card">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] sm:text-sm text-muted-foreground">Deps</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-0.5 sm:mt-1">{depCount}</p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 sm:w-6 sm:h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-card">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] sm:text-sm text-muted-foreground">Inactive</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-0.5 sm:mt-1">{inactiveCount}</p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg bg-gray-500/10 flex items-center justify-center flex-shrink-0">
                <UserX className="w-4 h-4 sm:w-6 sm:h-6 text-gray-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-card">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] sm:text-sm text-muted-foreground">Total</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-0.5 sm:mt-1">{players.length}</p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Music2 className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Player View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="glass border-white/10 !w-[calc(100vw-1rem)] !max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Player Details</DialogTitle>
          </DialogHeader>
          {viewingPlayer && (
            <div className="space-y-6 mt-4">
              {/* Basic Info - Visible to all */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">BASIC INFORMATION</h3>
                <div className="space-y-3">
                  <div className="flex justify-between gap-4 flex-wrap">
                    <span className="text-muted-foreground shrink-0">Name</span>
                    <span className="font-medium text-right break-words">{viewingPlayer.name}</span>
                  </div>
                  <div className="flex justify-between gap-4 flex-wrap">
                    <span className="text-muted-foreground shrink-0">Type</span>
                    {getPlayerTypeBadge(viewingPlayer.player_type)}
                  </div>
                  <div className="flex justify-between gap-4 flex-wrap">
                    <span className="text-muted-foreground shrink-0">Email</span>
                    <span className="font-medium text-right break-all">{viewingPlayer.email}</span>
                  </div>
                  <div className="flex justify-between gap-4 flex-wrap">
                    <span className="text-muted-foreground shrink-0">Phone</span>
                    <span className="font-medium text-right break-words">{viewingPlayer.phone || "—"}</span>
                  </div>
                  <div className="flex justify-between gap-4 flex-wrap">
                    <span className="text-muted-foreground shrink-0">Section(s)</span>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {getPlayerSections(viewingPlayer).length > 0 ? (
                        getPlayerSections(viewingPlayer).map((section, idx) => (
                          <Badge key={idx} variant="outline" className="border-primary/30 text-primary">
                            {section}
                          </Badge>
                        ))
                      ) : (
                        <span className="font-medium">Unassigned</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* iter-149h: Dep-only sections (Dep info + Gigs attended) */}
              {viewingPlayer.player_type === "dep" && showAdminControls && (
                <>
                  <Separator className="bg-white/10" />
                  <div>
                    <h3 className="text-sm font-medium text-primary mb-3">DEP INFO</h3>
                    <div className="space-y-3 p-4 rounded-lg bg-primary/5 border border-primary/10">
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Home band</span>
                        <span className="font-medium text-right break-words">{viewingPlayer.home_band || "—"}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Known by</span>
                        <span className="font-medium text-right break-words">
                          {(() => {
                            const kid = viewingPlayer.known_by_player_id;
                            if (!kid) return "—";
                            const p = players.find((x) => x.id === kid);
                            return p ? p.name : "—";
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-white/10" />
                  <div data-testid="dep-gigs-section">
                    <h3 className="text-sm font-medium text-primary mb-3 flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" />
                      GIGS ATTENDED
                      {depGigs.length > 0 && (
                        <Badge variant="outline" className="ml-auto text-xs">{depGigs.length}</Badge>
                      )}
                    </h3>
                    {depGigsLoading ? (
                      <p className="text-sm text-muted-foreground">Loading…</p>
                    ) : depGigs.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No past gigs recorded for this dep.</p>
                    ) : (
                      <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {depGigs.map((g) => (
                          <li
                            key={g.id}
                            className="p-3 rounded-lg bg-secondary/30 hover:bg-secondary/40 transition overflow-hidden"
                            data-testid={`dep-gig-${g.id}`}
                          >
                            <div className="flex items-baseline justify-between gap-3 min-w-0">
                              <span className="font-medium truncate min-w-0 flex-1">{g.title}</span>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {g.start_time ? new Date(g.start_time).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                              </span>
                            </div>
                            {(g.event_type || g.location) && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                {g.event_type ? g.event_type.replace(/_/g, " ") : ""}
                                {g.event_type && g.location ? " · " : ""}
                                {g.location || ""}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}

              {/* Admin-only sections */}
              {showAdminControls && (
                <>
                  <Separator className="bg-white/10" />

                  {/* Emergency Contact */}
                  <div>
                    <h3 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      EMERGENCY CONTACT
                    </h3>
                    {viewingPlayer.emergency_contact_name ? (
                      <div className="space-y-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                        <div className="flex justify-between gap-4 flex-wrap">
                          <span className="text-muted-foreground shrink-0">Name</span>
                          <span className="font-medium text-right break-words">{viewingPlayer.emergency_contact_name}</span>
                        </div>
                        <div className="flex justify-between gap-4 flex-wrap">
                          <span className="text-muted-foreground shrink-0">Phone</span>
                          <span className="font-medium text-right break-words">{viewingPlayer.emergency_contact_phone || "—"}</span>
                        </div>
                        <div className="flex justify-between gap-4 flex-wrap">
                          <span className="text-muted-foreground shrink-0">Relationship</span>
                          <span className="font-medium text-right break-words">{viewingPlayer.emergency_contact_relationship || "—"}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No emergency contact on file</p>
                    )}
                  </div>

                  <Separator className="bg-white/10" />

                  {/* Uniform Sizes */}
                  <div>
                    <h3 className="text-sm font-medium text-primary mb-3 flex items-center gap-2">
                      <Shirt className="w-4 h-4" />
                      UNIFORM SIZES
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-secondary/30 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Jacket</p>
                        <p className="text-xl font-bold">{viewingPlayer.jacket_size || "—"}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-secondary/30 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Shirt</p>
                        <p className="text-xl font-bold">{viewingPlayer.shirt_size || "—"}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="ghost" onClick={() => setViewDialogOpen(false)}>
                  Close
                </Button>
                {showAdminControls && (
                  <Button 
                    className="bg-primary text-primary-foreground"
                    onClick={() => {
                      setViewDialogOpen(false);
                      handleEdit(viewingPlayer);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Player
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter-78: Player-type tabs replace the old "Show deps" toggle.
          Admins see all 4 tabs; non-admins only Members + Deps.
          iter-81: gate on isAdmin (role) rather than showAdminControls
          (admin-mode toggle) so admins see all 4 even in "Member View"
          mode — matches the Inactive stat card which is always visible. */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${isAdmin ? "grid-cols-2 grid-rows-2 sm:grid-cols-4 sm:grid-rows-1 h-auto" : "grid-cols-2"}`}>
          <TabsTrigger value="member" data-testid="players-tab-member">
            Members <Badge variant="outline" className="ml-2 text-[10px]">{memberCount}</Badge>
          </TabsTrigger>
          <TabsTrigger value="dep" data-testid="players-tab-dep">
            Deps <Badge variant="outline" className="ml-2 text-[10px]">{depCount}</Badge>
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="info_only" data-testid="players-tab-info_only">
              Info Only <Badge variant="outline" className="ml-2 text-[10px]">{infoOnlyCount}</Badge>
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="inactive" data-testid="players-tab-inactive">
              Inactive <Badge variant="outline" className="ml-2 text-[10px]">{inactiveCount}</Badge>
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {/* Players Table */}
      <Card className="glass-card">
        <CardHeader className="border-b border-white/5 pb-4 flex flex-row items-center justify-between gap-2">
          <CardTitle className="font-heading text-lg">All Players</CardTitle>
          {useFlatView && (
            <button
              type="button"
              onClick={playersCtl.clearAll}
              className="text-xs text-primary hover:underline"
              data-testid="players-clear-sort-filter"
            >
              Clear sort & filters
            </button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {filteredPlayers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="[&_td]:p-1 [&_th]:p-1 md:[&_td]:p-2 md:[&_th]:p-2 text-xs md:text-sm">
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  {PLAYERS_COLUMNS.filter((c) => c.key !== "uniform" || showAdminControls).map((col) => (
                    <SortableTableHead
                      key={col.key}
                      column={col}
                      sortKey={playersCtl.sortKey}
                      sortDir={playersCtl.sortDir}
                      onToggleSort={() => playersCtl.toggleSort(col.key)}
                      rows={filteredPlayers}
                      selectedFilters={playersCtl.filters[col.key]}
                      onChangeFilter={(set) => playersCtl.setColumnFilter(col.key, set)}
                      className={col.mobileHidden ? "hidden md:table-cell" : ""}
                    />
                  ))}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {useFlatView ? (
                  playersCtl.processedRows.map((player) => renderPlayerRow(player))
                ) : (
                  playersBySection.map((group) => (
                  <Fragment key={group.key}>
                    {/* Iter-66j: small section header row inserted between groups */}
                    <TableRow
                      className="border-white/5 hover:bg-transparent bg-primary/[0.06]"
                      data-testid={`players-section-header-${group.key}`}
                    >
                      <TableCell
                        colSpan={showAdminControls ? 7 : 6}
                        className="py-1.5 px-4"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] uppercase tracking-wider text-primary font-semibold">
                            {group.name}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {group.rows.length} {group.rows.length === 1 ? "player" : "players"}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                    {group.rows.map((player) => renderPlayerRow(player))}
                  </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{searchTerm || filterType !== "all" ? "No players found matching your filters" : "No players added yet"}</p>
              {showAdminControls && !searchTerm && filterType === "all" && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setDialogOpen(true)}
                >
                  Add Your First Player
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* iter-107: Bulk-invite a dep to multiple upcoming events. */}
      <Dialog
        open={bulkInviteOpen}
        onOpenChange={(open) => {
          setBulkInviteOpen(open);
          if (!open) {
            setBulkInviteDep(null);
            setBulkInviteSelected([]);
            setBulkInviteSectionId("");
            setBulkInviteMessage("");
            setBulkInviteEvents([]);
          }
        }}
      >
        <DialogContent
          className="glass border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="bulk-invite-dep-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Invite {bulkInviteDep?.name || "dep"} to events
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Tick the upcoming events you want to invite{" "}
              <span className="font-medium text-foreground">{bulkInviteDep?.name}</span>{" "}
              to. They&apos;ll get one email per invitation and can accept/decline
              each one independently. Events they&apos;re already invited to are
              skipped automatically.
            </p>

            <div className="flex justify-end gap-2 text-xs">
              <button
                type="button"
                className="text-blue-300 hover:text-blue-200 underline"
                onClick={() => setBulkInviteSelected(bulkInviteEvents.map((e) => e.id))}
                data-testid="bulk-invite-select-all"
              >
                Select all
              </button>
              <span className="text-muted-foreground">|</span>
              <button
                type="button"
                className="text-blue-300 hover:text-blue-200 underline"
                onClick={() => setBulkInviteSelected([])}
                data-testid="bulk-invite-clear-all"
              >
                Clear
              </button>
            </div>

            {bulkInviteEventsLoading ? (
              <p className="text-sm text-muted-foreground">Loading upcoming events…</p>
            ) : bulkInviteEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No upcoming events.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1 rounded-lg border border-white/10 p-3 bg-secondary/30">
                {bulkInviteEvents.map((ev) => {
                  const checked = bulkInviteSelected.includes(ev.id);
                  const when = ev.start_time
                    ? new Date(ev.start_time).toLocaleString("en-GB", {
                        weekday: "short", day: "numeric", month: "short",
                        hour: "2-digit", minute: "2-digit",
                      })
                    : "";
                  return (
                    <label
                      key={ev.id}
                      className="flex items-start gap-3 cursor-pointer rounded p-2 hover:bg-white/5"
                      data-testid={`bulk-invite-event-${ev.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          setBulkInviteSelected((prev) => (
                            v
                              ? [...new Set([...prev, ev.id])]
                              : prev.filter((id) => id !== ev.id)
                          ));
                        }}
                        data-testid={`bulk-invite-event-cb-${ev.id}`}
                      />
                      <div className="flex-1 text-sm">
                        <p className="font-medium">
                          {ev.title}
                          <Badge variant="outline" className="ml-2 text-[10px]">
                            {ev.event_type}
                          </Badge>
                          {ev.attendance_status && (
                            <Badge variant="outline" className="ml-1 text-[10px] capitalize">
                              {ev.attendance_status.replace(/_/g, " ")}
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {when}{ev.location ? ` · ${ev.location}` : ""}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="space-y-2">
              <Label>
                Section <span className="text-red-400">*</span>
              </Label>
              <Select
                value={bulkInviteSectionId || ""}
                onValueChange={(v) => setBulkInviteSectionId(v)}
                disabled={!bulkInviteDep || (bulkInviteDep.section_ids || []).length === 0}
              >
                <SelectTrigger className="bg-secondary/50" data-testid="bulk-invite-section-select">
                  <SelectValue placeholder="Please select" />
                </SelectTrigger>
                <SelectContent>
                  {sections
                    .filter((s) => (bulkInviteDep?.section_ids || []).includes(s.id))
                    .map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {bulkInviteDep && (bulkInviteDep.section_ids || []).length === 0 && (
                <p className="text-[11px] text-amber-400">
                  This dep isn&apos;t assigned to any section. Edit their profile to add one before inviting.
                </p>
              )}
              {bulkInviteDep && (bulkInviteDep.section_ids || []).length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  Only sections this dep plays in are shown.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Input
                placeholder="Anything you want the dep to know…"
                value={bulkInviteMessage}
                onChange={(e) => setBulkInviteMessage(e.target.value)}
                className="bg-secondary/50"
                data-testid="bulk-invite-message"
              />
            </div>

            <div className="flex justify-between items-center pt-2">
              <p className="text-xs text-muted-foreground">
                {bulkInviteSelected.length} of {bulkInviteEvents.length} selected
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setBulkInviteOpen(false)}
                  data-testid="bulk-invite-cancel"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-primary text-primary-foreground"
                  disabled={bulkInviteSubmitting || bulkInviteSelected.length === 0}
                  onClick={submitBulkInvite}
                  data-testid="bulk-invite-submit"
                >
                  {bulkInviteSubmitting
                    ? "Sending…"
                    : `Send ${bulkInviteSelected.length} invitation${bulkInviteSelected.length === 1 ? "" : "s"}`}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Players;
